import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {BsStarFill } from "react-icons/bs";
import SongSimpliLogo from "../components/SongSimpliLogo";
import ThemeToggle from "../components/ThemeToggle";
import { ROUTE_CONSTANT } from "../routing/route-constants";
import { UNAUTH_DEFAULT_DURATION_MS } from "../constants";
import generateMusic from "../utils/generateMusic";
import RatingSvgIcon from "../assets/icons/ravecapture-logo-horizontal.svg"; 
import RatingSvgIconWhiteIcon from "../assets/icons/ravecapture-logo-white.svg";
import ProfileIcon from "../assets/icons/profile-icon.svg"
import useIsMobile from "../hooks/useIsMobile";
import {
  buildOnboardingPrompt,
  buildOnboardingSelections,
  buildSongForDisplayFilename,
  resolveOccasionValue,
  resolveRecipientValue,
  truncateToWords,
} from "../utils/onboardingData";
import { reportApiError } from "../utils/errorPopupBridge";
import { registerMusicDocProfile } from "../utils/musicExport";
import { getLocalizedPath, readCookie } from "../utils/helper";
import { resolveUserPaidStatus } from "../utils/operationLimit/userEntitlement";
import {
  clearOnboardingSession,
  loadOnboardingSession,
  saveOnboardingSession,
  type OnboardingSessionData,
} from "../utils/onboardingSession";
import { redirectToAuthPage } from "../utils/redirectToAuthPage";
import { redirectToPaymentPlansPage } from "../utils/redirectToPaymentPlansPage";
import HearOtherSongsSection from "@/components/HearOtherSongsSection";
import EditOrderDetailsModal from "@/components/EditOrderDetailsModal";


export default function SongSummaryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useParams<{ locale?: string }>();
  const localeVal = locale ?? "";
  const [sessionData, setSessionData] = useState<OnboardingSessionData | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDark, setIsDark] = useState(
    () => localStorage.getItem("theme") !== "light",
  );
  const isMobile = useIsMobile();

  useEffect(() => {
    const data = loadOnboardingSession();
    if (!data) {
      navigate(
        `${getLocalizedPath(localeVal, {
          Path: ROUTE_CONSTANT.MUSIC_EDITOR,
          LocalePath: ROUTE_CONSTANT.MUSIC_EDITOR_LOCALE,
        })}?onboarding=true`,
        { replace: true },
      );
      return;
    }
    setSessionData(data);
  }, [navigate, localeVal]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
  }, [isDark]);

  useEffect(() => {
    function restoreSessionFromStorage() {
      const data = loadOnboardingSession();
      if (data) {
        setSessionData(data);
      }
    }

    function handlePageShow() {
      restoreSessionFromStorage();
    }

    function handlePageHide() {
      if (sessionData) {
        saveOnboardingSession(sessionData);
      }
    }

    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [sessionData]);

  const toggleDark = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  const handleEdit = () => {
    if (isCreating) return;
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = (updated: OnboardingSessionData) => {
    saveOnboardingSession(updated);
    setSessionData(updated);
  };

  const handleCreateSong = async () => {
    if (!sessionData || isCreating) return;

    setIsCreating(true);
    setIsEditModalOpen(false);
    try {
      const { isLoggedIn, isUserPaid } = await resolveUserPaidStatus();

      if (isLoggedIn && !isUserPaid) {
        redirectToPaymentPlansPage(localeVal);
        return;
      }

      const prompt = buildOnboardingPrompt(sessionData);
      const guidedSelections = buildOnboardingSelections(sessionData);

      const result = await generateMusic({
        prompt,
        forcedInstrumental: false,
        guidedSelections,
        musicLen: isLoggedIn ? undefined : UNAUTH_DEFAULT_DURATION_MS,
      });

      const recipientLabel =
        sessionData.recipientName.trim() || resolveRecipientValue(sessionData) || "";
      const resultForHandoff = {
        ...result,
        filename: buildSongForDisplayFilename(recipientLabel, result.filename),
      };

      if (!isLoggedIn) {
        saveOnboardingSession(sessionData);
        await redirectToAuthPage(resultForHandoff, "account/signup", localeVal);
        return;
      }

      if (isUserPaid) {
        const docId = await registerMusicDocProfile(resultForHandoff);
        if (!docId) {
          throw new Error("Failed to register music doc profile.");
        }

        const userId = readCookie("userguid");
        if (!userId) {
          throw new Error("User guid not found.");
        }

        clearOnboardingSession();

        const playerPath = getLocalizedPath(localeVal, {
          Path: ROUTE_CONSTANT.MUSIC_PLAYER,
          LocalePath: ROUTE_CONSTANT.MUSIC_PLAYER_LOCALE,
        });

        navigate(
          `${playerPath}?uId=${encodeURIComponent(userId)}&docId=${encodeURIComponent(docId)}`,
          { replace: true },
        );
        return;
      }

      // TODO: re-enable music editor redirect for logged-in unpaid users when ready
      // navigate(ROUTE_CONSTANT.MUSIC_EDITOR, {
      //   replace: true,
      //   state: {
      //     initialMusicResult: result,
      //     initialPrompt: prompt,
      //     initialSelections: guidedSelections,
      //   },
      // });
    } catch (error) {
      console.error("Failed to create song:", error);
      reportApiError(error);
    } finally {
      setIsCreating(false);
    }
  };

  if (!sessionData) {
    return null;
  }

  const reviewRecipientName =
    sessionData.recipientName.trim() || resolveRecipientValue(sessionData) || "—";
  const reviewOccasion = resolveOccasionValue(sessionData) || "—";
  const reviewGenre =
    sessionData.selectedGenres.length > 0
      ? sessionData.selectedGenres.join(", ")
      : "—";
  const reviewQualitiesFull =
    sessionData.recipientSpecialDescription.trim() || "—";
  const reviewQualities = truncateToWords(reviewQualitiesFull, 4);

  return (
    <div
      className="relative flex h-screen w-full flex-col overflow-hidden"
      style={{
        background: isDark
          ? "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(155, 42, 168, 0.6) 0%, rgba(155, 42, 168, 0.3) 50%, transparent 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #eee3ff 64%, #b3e2f0 100%)",
      }}
    >
      <div className={`shrink-0 z-20 flex flex-col sm:flex-row sm:items-center sm:justify-between sm:py-5 p-[10px] sm:px-5 gap-0 ${isDark ? "border-b border-[#EAEBED] sm:border-b-0" : "border-b border-[#EAEBED] sm:border-b-0"}`}>
        <div className="flex items-center justify-between sm:justify-start gap-[4.29px]">
          <SongSimpliLogo isDark={isDark} />
          <div className="sm:hidden">
            <ThemeToggle isDark={isDark} onToggle={toggleDark} />
          </div>
        </div>

        <div className="flex-1" />

        <div className="hidden sm:block">
          <ThemeToggle isDark={isDark} onToggle={toggleDark} />
        </div>
      </div>

      <div className={`flex flex-1 flex-col items-center w-full overflow-x-hidden overflow-y-auto justify-start py-4 pb-32 sm:pb-[120px] px-0 sm:px-0 `}>
        <div className="w-full flex flex-col items-center">
          <div className="flex w-full flex-col gap-4 px-[10px] sm:px-0">
              <div className="flex w-full flex-col items-center gap-1 sm:gap-2 px-4 sm:px-0">
                <h1
                  className={`max-w-[300px] sm:max-w-none text-[20px] sm:text-[36px] lg:text-[44px] sm:font-semibold font-bold tracking-[0.88px] leading-normal text-center ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
                >
                  {t("onboarding.review.heading")}
                </h1>
                <p
                  className={`max-w-[318px] sm:max-w-[1100px] text-[12px] sm:text-xl lg:text-[24px] font-normal tracking-[0.48px] text-center ${isDark ? "text-[#FDFDFD]" : "sm:text-[#16283D] text-[#16283D]"}`}
                >
                  {t("onboarding.review.subheading", { name: reviewRecipientName })}
                </p>
              </div>
              <div className="max-w-170 w-full mx-auto flex flex-col gap-4 sm:mt-[40px]">
              <div
                className={`w-full rounded-[10px] border-2 px-4 py-4 sm:px-6 sm:py-5 ${
                  isDark ? "border-[#264a73] bg-[#1e3554]" : "border-[#d2e5ff] bg-white"
                }`}
              >
                <div className="mb-4 flex items-center justify-between gap-3">
                  <span
                    className={`text-[16px] sm:text-[18px] font-bold ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}
                  >
                    {t("onboarding.review.orderSummary")}
                  </span>
                  <button
                    type="button"
                    id="editOrderSummary"
                    onClick={handleEdit}
                    disabled={isCreating}
                    className={`flex items-center gap-1.5 text-[14px]  sm:text-[16px] font-normal  ${isDark ? "text-[#fdfdfd]" : "text-[#687585]"} ${
                      isCreating ? "cursor-not-allowed opacity-50" : ""}`}
                  ><svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10.156 2.34403L12.9181 5.10617M11.3279 1.17215C12.0906 0.409412 13.3273 0.409412 14.09 1.17215C14.8528 1.9349 14.8528 3.17155 14.09 3.93429L3.33398 14.6903H0.599609V11.9004L11.3279 1.17215Z"
                        stroke={isDark ? "#fff" : "#687585"}
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    {t("onboarding.review.edit")}
                    
                  </button>
                </div>

                {[
                  { label: t("onboarding.review.songFor"), value: reviewRecipientName, fullValue: reviewRecipientName },
                  { label: t("onboarding.review.occasion"), value: reviewOccasion, fullValue: reviewOccasion },
                  { label: t("onboarding.review.genre"), value: reviewGenre, fullValue: reviewGenre },
                  {
                    label: t("onboarding.review.qualities"),
                    value: reviewQualities,
                    fullValue: reviewQualitiesFull,
                  },
                ].map(({ label, value, fullValue }, index) => (
                  <div
                    key={label}
                    className={`relative flex items-start justify-between gap-4 py-3 text-[12px] sm:text-[16px] ${
                      index === 0 ? "" : isDark ? "border-t border-[#264a73]" : "border-t border-[#eaebed]"
                    }`}
                  >
                    <span className={` min-h-[25px] ${isDark ? "text-[#fff]" : "text-[#16283D]"}`}>{label}</span>
                    <span
                      className={`group relative min-w-0 max-w-[55%] min-h-[25px] text-right font-normal ${isDark ? "text-[#A1A7B0]" : "text-[#A1A7B0]"}`}
                    >
                      <span className="block truncate min-h-[25px]">{value}</span>
                      {fullValue && (
                        <span
                          className="pointer-events-none absolute bottom-full right-0 z-10 mb-2 whitespace-normal break-words rounded-lg bg-white text-[#16283D] dark:bg-[#1F2937] dark:text-white px-3 py-1.5 text-left text-[12px] font-normal opacity-0 shadow-xl transition-opacity duration-150 group-hover:opacity-100"
                          style={{ maxWidth: "min(90vw, 320px)" }}
                        >
                          {fullValue}
                          <span className="absolute right-3 top-full h-2 w-2 -translate-y-1 rotate-45 bg-white dark:bg-[#1F2937]" />
                        </span>
                      )}
                    </span>
                  </div>
                ))}

                <div
                  className={`flex flex-col gap-3 text-[12px] sm:text-[16px]`}
                >
                  <div className={`flex items-center justify-between gap-3 border-t py-3 ${
                    isDark ? "border-[#264a73]" : "border-[#eaebed]"
                  }`}>
                     
                    <span className={`group relative max-w-[55%] min-h-[25px] ${isDark ? "text-[#fff]" : "text-[#16283D]"}`}>
                      <span className="items-center block truncate py-[5px]">
                        {t("onboarding.review.yourSongFor", { name: reviewRecipientName })}
                      </span>
                       <span
                        className="pointer-events-none min-h-[25px] absolute bottom-full left-0 z-10 mb-2 whitespace-normal break-words rounded-lg bg-white text-[#16283D] dark:bg-[#1F2937] dark:text-white px-3 py-1.5 text-left text-[12px] font-normal opacity-0 shadow-xl transition-opacity duration-150 group-hover:opacity-100"
                        style={{ maxWidth: "min(90vw, 320px)" }}
                      >
                        {reviewRecipientName}
                        <span className="absolute left-3 top-full h-2 w-2 -translate-y-1 rotate-45 bg-white dark:bg-[#1F2937]" />
                      </span>
                    </span>
                   
                      
                    <div className="flex sm:flex-row flex-col items-center gap-1">
                      <span className={`rounded-full  px-2 py-1.5 sm:text-[14px] text-[12px] font-bold ${isDark ? "bg-[#17AE06] text-[#E8F7E7]":"bg-[#E8F7E7] text-[#0D8000]"}`}>
                        {t("onboarding.review.save", { amount: t("onboarding.review.saveAmount") })}
                      </span>
                      <div className="flex items-center gap-2 min-h-[25px] ">
                        <span className={`text-[14px] line-through ${isDark ? "text-[#fff]" : "text-[#a1a7b0]"}`}>
                          {t("onboarding.review.originalPrice")}
                        </span>
                        <span className={`font-bold ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}>
                          {t("onboarding.review.totalPrice")}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={`flex items-center justify-between gap-3 border-t py-3 ${
                    isDark ? "border-[#264a73]" : "border-[#eaebed]"
                  }`}>
                    <span className={isDark ? "text-[#fff]" : "text-[#16283D]"}>
                      {t("onboarding.review.unlimitedRedos")}
                    </span>
                    <span className={`font-medium ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}>
                      {t("onboarding.review.free")}
                    </span>
                  </div>
                </div>

                <div
                  className={`mt-2 flex items-center justify-between border-t pt-4 text-[12px] sm:text-[18px] font-bold ${
                    isDark ? "border-[#264a73] text-[#fdfdfd]" : "border-[#eaebed] text-[#16283d]"
                  }`}
                >
                  <span>{t("onboarding.review.total")}</span>
                  <span>{t("onboarding.review.totalPrice")}</span>
                </div>
              </div>

              <HearOtherSongsSection isDark={isDark} />

              <div
                className={`w-full rounded-[10px] border-2 px-4 py-4 sm:px-6 sm:py-5 ${
                  isDark ? "border-[#264a73] bg-[#1e3554]" : "border-[#d2e5ff] bg-white"
                }`}
              >
                <div className="mb-4 flex flex-wrap items-center   gap-2 sm:justify-start">
                  {isDark ? <img src={RatingSvgIconWhiteIcon} alt="Rating" className="inline-block mr-1 w-[100px]" /> : <img src={RatingSvgIcon} alt="Rating" className="inline-block mr-1" />}
                 {/* <span className={`text-[14px] font-bold tracking-[0.28px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}>
                    {t("onboarding.review.reviewsBrand")}
                  </span> */}
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, index) => (
                       <div
                        key={index}
                        className="flex h-[20px] w-[20px] p-[2px]  items-center justify-center rounded-[2px] mr-1"
                        style={{
                          background:
                            index < 4
                              ? "#1a73e8"
                              : "linear-gradient(90deg, #1a73e8 50%, #dbe8ff 50%)",
                        }}
                      >
                        <BsStarFill className="text-white" size={14} />
                      </div>
                    ))}
                  </div>
                  <div
                    className={`text-[10.0px] font-semibold sm:text-[10px]  ${isDark ? "text-[#fdfdfd]" : "text-[#223047]"}`}
                    dangerouslySetInnerHTML={{ __html: t("onboarding.review.reviewsCount") }}
                  />
                </div>

                <div
                  className={`rounded-[10px] border p-4 ${
                    isDark ? "border-[#0e76ff] bg-[#264a73]" : "border-[#d2e5ff] bg-[#f8fbff]"
                  }`}
                >
                  <div className="mb-3 flex items-center gap-3">
                    <div className="flex shrink-0 items-center justify-center rounded-full bg-[#d2e5ff] text-[14px] font-bold text-[#0e76ff]">
                      <img src={ProfileIcon} alt="profile-icon" className="h-[44px] w-[44px] rounded-2xl" />
                    </div>
                    <div>
                      <p className={`text-[14px] font-semibold sm:mb-[6px] ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}>
                        {t("onboarding.review.reviewerName")}
                      </p>
                      <p className={`text-[12px] ${isDark ? "text-[#fff]" : "text-[#687585]"}`}>
                        {t("onboarding.review.reviewerTitle")}
                      </p>
                    </div>
                  </div>
                  <div className="mb-2 sm:mb-3 flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, index) => (
                      // <BsStarFill key={index} className="text-[#0e76ff]" size={12} />
                       <div
                        key={index}
                        className="flex h-[20px] w-[20px] p-[2px]  items-center justify-center rounded-[2px] mr-1"
                        style={{
                          background:
                            index < 4
                              ? "#1a73e8"
                              : "linear-gradient(90deg, #1a73e8 50%, #dbe8ff 50%)",
                        }}
                      >
                        <BsStarFill className="text-white" size={14} />
                      </div>
                    ))}
                      <div
                    className={`text-[10.0px] font-semibold sm:text-[10px]  ${isDark ? "text-[#fdfdfd]" : "text-[#223047]"}`}
                    dangerouslySetInnerHTML={{ __html: t("onboarding.review.reviewsCount") }}
                  />
                  </div>
                  <p
                    className={`text-[14px] sm:text-[16px] font-semibold leading-normal sm:mb-3 ${isDark ? "text-[#fdfdfd]" : "text-[#16283D]"}`}
                    dangerouslySetInnerHTML={{
                      __html: isMobile
                        ? t("onboarding.review.reviewTextHeading")
                        : t("onboarding.review.reviewTextHeading").replace(/<br\s*\/?>/gi, " "),
                    }}
                  />
                   <p className={`text-[14px] sm:text-[18px]  leading-normal ${isDark ? "text-[#fdfdfd]" : "text-[#16283d]"}`}>
                    {t("onboarding.review.reviewText")}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className={`flex flex-col p-4 items-center w-full mx-auto mt-[15px] sm:mt-[10px] sm:mx-0 fixed bottom-0 inset-x-0 z-30 ${isDark ? "bg-[#16283d]" : "bg-white"}`} style={isDark ? { boxShadow: "0 -8px 15px -8px rgba(0, 0, 0, 0.15)" } : undefined}>
            <button
              type="button"
              onClick={() => void handleCreateSong()}
              disabled={isCreating}
              aria-busy={isCreating}
              id="createMySong"
              className={`flex justify-center items-center sm:h-[60px] h-[52px] mb-2 uppercase rounded-full px-8 sm:px-20 py-3 sm:py-4 sm:text-[18px] font-bold tracking-[0.36px] max-w-[470px] text-white w-full ${
                isCreating ? "cursor-not-allowed opacity-70" : "cursor-pointer"
              }`}
              style={{
                background: "linear-gradient(90deg, #f95a38 0%, #fc792e 50%, #ff9823 100%)",
              }}
            >
              {isCreating && (
                <span
                  className="inline-block size-5 shrink-0 animate-spin rounded-full border-2 border-white border-t-transparent"
                  aria-hidden
                />
              )}
              {t("onboarding.createMySong")}
            </button>
            <span className="sm:mt-[10px] text-[#687585] dark:text-[#DBDEE1]">{t("onboarding.review.seepricing")} </span>
          </div>
        </div>
      </div>

      <EditOrderDetailsModal
        isOpen={isEditModalOpen}
        isDark={isDark}
        data={sessionData}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveEdit}
      />
    </div>
  );
}
